/* Progressive infinite scrolling for Shopify collection and search grids.
 * Keeps ordinary pagination links in the HTML for accessibility and fallback.
 */
(() => {
  if (!('IntersectionObserver' in window) || !('fetch' in window)) return;

  let activePager = null;
  let observer = null;
  let busy = false;

  const getGrid = (root) => {
    const container = root.querySelector('#ProductGridContainer');
    if (!container) return null;
    const grid = container.querySelector('#product-grid');
    return grid?.matches('ul') ? grid : grid?.querySelector('ul');
  };

  function connect() {
    const container = document.querySelector('#ProductGridContainer');
    const pager = container?.querySelector('.pagination-wrapper');
    if (!container || !pager || pager === activePager || !getGrid(document)) return;

    observer?.disconnect();
    activePager = pager;
    busy = false;

    const nextLink = () => pager.querySelector('a[aria-label*="next" i]');
    if (!nextLink()) {
      pager.remove();
      activePager = null;
      return;
    }

    // Use a separate visible sentinel so hiding page numbers doesn't disable observation.
    pager.style.display = 'none';
    const sentinel = document.createElement('div');
    sentinel.className = 'phannies-infinite-scroll-status';
    sentinel.setAttribute('role', 'status');
    sentinel.setAttribute('aria-live', 'polite');
    // Dawn hides all div:empty elements, so the observer target must contain text.
    sentinel.textContent = '\u00a0';
    sentinel.style.cssText = 'text-align:center;padding:20px;font-size:14px;min-height:50px';
    pager.after(sentinel);

    observer = new IntersectionObserver(async (entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || busy) return;
      const link = nextLink();
      if (!link) {
        observer.disconnect();
        sentinel.remove();
        return;
      }
      busy = true;
      sentinel.textContent = 'Loading more products…';
      try {
        const response = await fetch(link.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Page request failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const destination = getGrid(document);
        const incoming = getGrid(html);
        if (!destination || !incoming || !destination.isConnected || activePager !== pager) return;

        const cards = Array.from(incoming.children);
        if (!cards.length) throw new Error('No additional results found');
        destination.append(...cards.map((card) => {
          const imported = document.importNode(card, true);
          // Dawn's initial scroll-animation observer may not register newly loaded cards.
          // Keep their content visible instead of leaving it hidden at opacity zero.
          [imported, ...imported.querySelectorAll('.scroll-trigger')].forEach((element) => {
            element.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in');
          });
          return imported;
        }));

        const newPager = html.querySelector('#ProductGridContainer .pagination-wrapper');
        if (newPager) {
          pager.replaceWith(document.importNode(newPager, true));
          activePager = null;
          observer.disconnect();
          sentinel.remove();
          connect();
        } else {
          observer.disconnect();
          pager.remove();
          sentinel.remove();
          activePager = null;
        }
      } catch (error) {
        // Restore the native links when an error occurs, so shopping can continue.
        console.warn('Infinite loading unavailable; showing page links.', error);
        observer.disconnect();
        pager.style.display = '';
        sentinel.remove();
      } finally {
        busy = false;
      }
    }, { rootMargin: '650px 0px', threshold: 0 });
    observer.observe(sentinel);
  }

  let scheduled = false;
  const watch = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      connect();
    });
  });
  const start = () => {
    connect();
    watch.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
